import { useMemo } from 'react';
import {
  formatProductDiscountText,
  getBogoFromProduct,
  getBogoSpecialURL,
  getBundleFromProduct,
  getTargetPriceDiscountFromProduct,
} from '@/utils';
import { GProduct, Variant } from '@/types';
import { useData } from '../components';

const useActiveProductBadges = ({
  product,
  weightVariant,
  isDetail = false,
  badgeClass,
}: ActiveBadgesParams) => {
  const { productCard } = useData();
  const bogo = getBogoFromProduct(product);
  const bundle = getBundleFromProduct(product);
  const targetPrice = getTargetPriceDiscountFromProduct(product);
  // IDs of discounts that were actually applied during OpenSearch price calculation
  const discountsApplied = product?.pricing?.applied_discount_ids || [];

  // Extract IDs of PERCENT and DOLLAR discounts (not TARGET_PRICE) for badge display validation
  const percentOrDollarDiscountIds = product?.discounts
    .filter(
      discount =>
        (discount?.discount_method === 'DOLLAR' ||
          discount?.discount_method === 'PERCENT') &&
        discount?.discount_affinity === 'Pre-Cart',
    )
    .map(discount => discount.discount_id);

  // Check if TARGET_PRICE discount was actually applied (important when non-stackable discounts compete)
  const isTargetPriceApplied = useMemo(
    () => !!targetPrice && discountsApplied?.includes(targetPrice?.discount_id),
    [targetPrice, discountsApplied],
  );

  // Only use TARGET_PRICE discount amount if it was actually applied, otherwise 0
  const targetPriceDiscount = isTargetPriceApplied
    ? targetPrice?.discount_amount
    : 0;

  const productDiscountText = useMemo(
    () =>
      formatProductDiscountText({
        format: productCard?.discount_display ?? 'percentage',
        value: weightVariant,
        targetPrice: targetPriceDiscount,
      }),
    [weightVariant, productCard?.discount_display, targetPriceDiscount],
  );

  // Validate that at least one PERCENT/DOLLAR discount was actually applied before showing badge
  const isDiscountTextApplied = useMemo(
    () =>
      !!productDiscountText &&
      discountsApplied?.some(discountId =>
        percentOrDollarDiscountIds?.includes(discountId),
      ),
    [productDiscountText, discountsApplied, weightVariant?.id],
  );

  const hasStockFlags = !!(
    product?.isLowStock ||
    product?.isOutStock ||
    productDiscountText
  );

  const hasPromotions = !!(bogo || bundle);

  const showPromoBadges = !isDetail && hasPromotions;

  const shouldRender = hasStockFlags || showPromoBadges || !!targetPrice;

  const badges = useMemo(() => {
    const allBadgeConfigs: (BadgeConfig & { condition: boolean })[] = [
      {
        condition: !!product.isLowStock,
        props: {
          key: 'badge-low-stock',
          label: 'Low Stock',
          type: 'warning',
        },
      },
      {
        condition: !!product.isOutStock,
        props: {
          key: 'badge-out-of-stock',
          label: 'Out of Stock',
          type: 'warning',
        },
      },
      {
        condition: !isDetail && !!bogo,
        props: {
          key: 'badge-bogo',
          label: productCard?.bogo_badge_label ?? 'BOGO',
          type: 'bogo',
          iconName: 'gift',
          link: bogo ? getBogoSpecialURL(bogo) : undefined,
          showIcon: true,
        },
      },
      {
        condition: !isDetail && !!bundle,
        props: {
          key: 'badge-bundle',
          label: productCard?.bundle_badge_label ?? 'BUNDLE',
          type: 'bogo',
          iconName: 'price-tag',
          link: bundle ? getBogoSpecialURL(bundle) : undefined,
          showIcon: true,
        },
      },
      {
        condition: !!isDiscountTextApplied,
        props: {
          key: 'badge-discount',
          label: productDiscountText || '',
          type: 'discount',
        },
      },
      {
        condition: !!isTargetPriceApplied,
        props: {
          key: 'badge-target-price',
          label: `$${targetPrice?.discount_amount || 0} price`,
          type: 'discount',
        },
      },
    ];

    return allBadgeConfigs
      .filter(badge => badge.condition)
      .map(({ props }) => ({
        props: {
          ...props,
          className: badgeClass,
        },
      }));
  }, [
    product.isLowStock,
    product.isOutStock,
    bogo,
    bundle,
    targetPrice,
    productDiscountText,
    isDetail,
    badgeClass,
    productCard?.bogo_badge_label,
    productCard?.bundle_badge_label,
  ]);

  return {
    shouldRender,
    badges,
  };
};

interface ActiveBadgesParams {
  product: GProduct;
  weightVariant: Variant;
  isDetail?: boolean;
  badgeClass?: string;
}

interface BadgeConfig {
  props: {
    className?: string;
    key: string;
    label: string;
    type: 'warning' | 'bogo' | 'discount';
    iconName?: 'gift' | 'price-tag';
    link?: string;
    showIcon?: boolean;
  };
}

export { useActiveProductBadges };
